import {
  localDateKey,
  nextLocalDayStart,
  zonedEpoch,
  type ActivityChartData,
  type ChartDay,
  type ChartDayMeta,
  type ChartLane,
  type ChartSegment,
  type CountryEvent,
  type CountryPeriod,
  type DriverPeriod,
  type TimeRange,
  type VehiclePeriod,
} from 'tacho-activity-chart';
import { buildCountryChartData, type PlaceLike } from './place-chart-data';
import { filterDiagramDays } from './diagram-period';
import type { DriverCountryPlaceLike, DriverDiagramDayLike } from './driver-chart-data';
import type { VehicleActivityLike, VehicleDiagramDayLike, VehicleDriverLike } from './vehicle-chart-data';

/**
 * Canonical, timezone-free view of a diagram endpoint.
 *
 * The API payload is grouped per day and mixes two time encodings:
 *  - activity times are **minutes since the UTC day start** (`day + minutes`),
 *  - roster, place, out-of-scope and ferry times are **absolute UTC epochs**.
 *
 * Everything below is normalised to absolute UTC epoch seconds first. The
 * display timezone is only applied in {@link toChartData}, where the rows are
 * laid out on the user's local calendar days.
 */

const MINUTE = 60;
const DAY = 24 * 60 * MINUTE;

/**
 * The vehicle endpoint reports the last rest of the day as `23:59`; it means
 * "until the end of the day", not literally one minute before midnight.
 */
const VEHICLE_END_SENTINEL = 1439;

const TYPE_NAMES: Record<number, string> = {
  0: 'BREAK/REST',
  1: 'AVAILABILITY',
  2: 'WORK',
  3: 'DRIVING',
  4: 'UNKNOWN',
  5: 'DRIVING',
  6: 'WORK',
  7: 'UNKNOWN',
};

/** One activity with absolute UTC instants. */
export interface NormalizedActivity {
  type: number;
  typeName: string;
  /** Epoch **seconds**, UTC. */
  start: number;
  /** Epoch **seconds**, UTC, exclusive. */
  end: number;
  driverName: string;
  cardNumber: string;
  /** Wire driving status: 1 single, 2 crew, 3 manual, 4 unknown. */
  drivingStatus?: number;
  /** Driver slot (`true`) or co-driver slot (`false`), vehicle endpoint only. */
  slot: boolean;
  entryType: string;
}

/** One API day, with its activities resolved to UTC instants. */
export interface NormalizedDay {
  /** UTC day start of the API bucket (`day`). */
  day: number;
  /** UTC calendar date of the bucket, `YYYY-MM-DD`. */
  date: string;
  activities: NormalizedActivity[];
  coDriverActivities: NormalizedActivity[];
  meta: ChartDayMeta;
  odoStart?: number | null;
  odoEnd?: number | null;
}

/** The whole period: day buckets plus the period-wide tracks. */
export interface NormalizedDiagram {
  days: NormalizedDay[];
  /** Every driver activity of the period, UTC, sorted by start. */
  activities: NormalizedActivity[];
  countries: CountryPeriod[];
  countryEvents: CountryEvent[];
  driverPeriods: DriverPeriod[];
  coDriverPeriods: DriverPeriod[];
  vehicles: VehiclePeriod[];
  outOfScope: TimeRange[];
  ferry: number[];
}

/* ------------------------------------------------------------------ */
/* Vehicle endpoint                                                    */
/* ------------------------------------------------------------------ */

export function normalizeVehicleDiagram(days: VehicleDiagramDayLike[] | null | undefined): NormalizedDiagram {
  const normalized: NormalizedDay[] = [];
  const driverPeriods: DriverPeriod[] = [];
  const coDriverPeriods: DriverPeriod[] = [];
  const places: PlaceLike[] = [];
  const outOfScope: TimeRange[] = [];
  const ferry: number[] = [];
  let fallbackEnd = 0;

  for (const raw of list(days)) {
    const day = numberOr(raw.day, 0);
    if (!(day > 0)) continue;

    const activities = vehicleActivities(raw.driverActivities, day);
    const coDriverActivities = vehicleActivities(raw.coDriverActivities, day);
    const roster = list(raw.drivers) as VehicleDriverLike[];
    const coRoster = list(raw.coDrivers) as VehicleDriverLike[];
    const allRoster = [...roster, ...coRoster];
    const first = roster[0];

    driverPeriods.push(...rosterPeriods(roster, activities, coDriverActivities));
    const fromRoster = rosterPeriods(coRoster, coDriverActivities, []);
    coDriverPeriods.push(...(fromRoster.length > 0 ? fromRoster : activityPeriods(coDriverActivities)));
    const dayPlaces = list(raw.places);
    places.push(...dayPlaces);
    outOfScope.push(...ranges(raw.outOfScopes));
    ferry.push(...instants(raw.ferryInfos));
    // Only days with actual data may extend an open country stay. Empty
    // buckets later in the selected period must not stretch the band.
    if (activities.length > 0 || coDriverActivities.length > 0 || dayPlaces.length > 0) {
      fallbackEnd = Math.max(fallbackEnd, day + DAY);
    }

    normalized.push({
      day,
      date: utcDate(day),
      activities,
      coDriverActivities,
      meta: {
        registrationNumber: text(raw.registrationNumber) || undefined,
        note: driverNames(allRoster, activities, coDriverActivities) || undefined,
      },
      odoStart: numberOr(first?.insertionOdo ?? first?.placeOdoBegin ?? first?.odoBegin, 0) || null,
      odoEnd: numberOr(first?.extractionOdo ?? first?.placeOdoEnd ?? first?.odoEnd, 0) || null,
    });
  }

  const countryData = places.length > 0
    ? buildCountryChartData(places, fallbackEnd)
    : { countries: [], countryEvents: [] };

  return {
    days: mergeDuplicateDays(normalized),
    activities: flatten(normalized),
    countries: countryData.countries,
    countryEvents: countryData.countryEvents,
    driverPeriods: dedupePeriods(driverPeriods),
    coDriverPeriods: dedupePeriods(coDriverPeriods),
    vehicles: [],
    outOfScope: mergeRanges(outOfScope),
    ferry: uniqueInstants(ferry),
  };
}

function vehicleActivities(
  records: unknown[] | null | undefined,
  day: number,
): NormalizedActivity[] {
  const out: NormalizedActivity[] = [];

  for (const raw of list(records)) {
    const record = raw as VehicleActivityLike;
    const startMinute = numberOr(record?.startTimeInMin, NaN);
    let endMinute = numberOr(record?.endTimeInMin, NaN);
    if (!Number.isFinite(startMinute) || !Number.isFinite(endMinute)) continue;

    if (endMinute === VEHICLE_END_SENTINEL) {
      endMinute = 24 * 60;
    } else if (endMinute < startMinute) {
      // The activity wraps past midnight inside the same day bucket.
      endMinute += 24 * 60;
    }

    const start = day + startMinute * MINUTE;
    const end = day + endMinute * MINUTE;
    // Zero-duration rows are card markers ("Card withdrawal" at hh:mm - hh:mm),
    // not activities; expanding them would swallow the rest of the day.
    if (!(end > start)) continue;

    const type = numberOr(record?.type, 0);
    out.push({
      type,
      typeName: text(record?.typeName) || TYPE_NAMES[type] || 'UNKNOWN',
      start,
      end,
      driverName: text(record?.driver),
      cardNumber: text(record?.cardNumber),
      drivingStatus: record?.status !== false ? 1 : undefined,
      slot: record?.slot !== false,
      entryType: text(record?.entryType) || 'Automatic',
    });
  }

  return sortActivities(out);
}

function rosterPeriods(
  roster: VehicleDriverLike[],
  activities: NormalizedActivity[],
  coDriverActivities: NormalizedActivity[],
): DriverPeriod[] {
  const periods: DriverPeriod[] = [];
  const all = [...activities, ...coDriverActivities];

  for (const driver of roster) {
    const driverName = text(driver?.driverName);
    const cardNumber = text(driver?.cardNumber) || undefined;
    if (!driverName) continue;

    let start = numberOr(driver?.insertionTime, 0);
    let end = numberOr(driver?.extractionTime ?? driver?.endTime, 0);

    if (!(end > start)) {
      const own = all.filter((activity) =>
        cardNumber && activity.cardNumber ? activity.cardNumber === cardNumber : activity.driverName === driverName,
      );
      if (own.length === 0) continue;
      start = Math.min(...own.map((activity) => activity.start));
      end = Math.max(...own.map((activity) => activity.end));
    }

    if (!(end > start)) continue;
    periods.push({ driverName, cardNumber, start, end });
  }

  return periods;
}

/** Card-inserted activity spans per co-driver, used when the roster is absent. */
function activityPeriods(activities: NormalizedActivity[]): DriverPeriod[] {
  const byDriver = new Map<string, { driverName: string; cardNumber?: string; start: number; end: number }>();

  for (const activity of activities) {
    if (activity.type >= 4) continue; // no-card rows have no co-driver identity
    const key = activity.cardNumber || activity.driverName;
    if (!key) continue;
    const current = byDriver.get(key);
    if (current) {
      current.start = Math.min(current.start, activity.start);
      current.end = Math.max(current.end, activity.end);
    } else {
      byDriver.set(key, {
        driverName: activity.driverName,
        cardNumber: activity.cardNumber || undefined,
        start: activity.start,
        end: activity.end,
      });
    }
  }

  return [...byDriver.values()].filter((period) => period.driverName && period.end > period.start);
}

/* ------------------------------------------------------------------ */
/* Driver endpoint                                                     */
/* ------------------------------------------------------------------ */

export function normalizeDriverDiagram(days: DriverDiagramDayLike[] | null | undefined): NormalizedDiagram {
  const normalized: NormalizedDay[] = [];
  const vehicles: VehiclePeriod[] = [];
  const places: PlaceLike[] = [];
  const outOfScope: TimeRange[] = [];
  const ferry: number[] = [];
  const events: CountryEvent[] = [];
  let fallbackEnd = 0;

  for (const raw of list(days)) {
    const day = numberOr(raw.day, 0);
    if (!(day > 0)) continue;

    const driverName = text(raw.driverName);
    const cardNumber = text(raw.cardNumber);
    const activities = driverActivities(raw.activities, day, driverName, cardNumber);
    const firstVehicle = list(raw.vehicles)[0] as Record<string, unknown> | undefined;

    const dayPlaces = list(raw.places);
    vehicles.push(...driverVehicles(raw.vehicles));
    places.push(...dayPlaces);
    outOfScope.push(...ranges(raw.outOfScopes));
    ferry.push(...instants(raw.ferryInfos));
    addCountryEvent(events, raw.startCountry, 'start');
    addCountryEvent(events, raw.endCountry, 'end');
    if (activities.length > 0 || dayPlaces.length > 0) {
      fallbackEnd = Math.max(fallbackEnd, day + DAY);
    }

    normalized.push({
      day,
      date: utcDate(day),
      activities,
      coDriverActivities: [],
      meta: {
        registrationNumber: text(firstVehicle?.['vehicleRegistrationNumber']) || undefined,
        vehicleId: text(firstVehicle?.['vehicleIdentificationNumber']) || undefined,
        note: list(raw.vehicles).length > 1 ? `${list(raw.vehicles).length} vehicles` : undefined,
      },
      odoStart: numberOr(firstVehicle?.['odoStart'], 0) || null,
      odoEnd: numberOr(firstVehicle?.['odoEnd'], 0) || null,
    });
  }

  let countries: CountryPeriod[];
  let countryEvents: CountryEvent[];

  if (places.length > 0) {
    const countryData = buildCountryChartData(places, fallbackEnd);
    countries = countryData.countries;
    countryEvents = countryData.countryEvents;
  } else {
    countryEvents = mergeCountryEvents(events);
    countries = countryPeriodsFromEvents(countryEvents, Math.max(fallbackEnd, lastActivityEnd(normalized)));
  }

  return {
    days: mergeDuplicateDays(normalized),
    activities: flatten(normalized),
    countries,
    countryEvents,
    driverPeriods: [],
    coDriverPeriods: [],
    vehicles: dedupeVehicles(vehicles),
    outOfScope: mergeRanges(outOfScope),
    ferry: uniqueInstants(ferry),
  };
}

function driverActivities(
  records: DriverDiagramDayLike['activities'] | null | undefined,
  day: number,
  driverName: string,
  cardNumber: string,
): NormalizedActivity[] {
  const out: NormalizedActivity[] = [];

  for (const record of list(records)) {
    // The endpoint is driver-scoped, so every row belongs to the requested
    // driver's card. That card can have been inserted in the co-driver slot
    // (cardSlot 2) for the whole period, so both slots must be drawn on the
    // driver lane; ignoring slot 2 made the chart empty for such drivers.

    // The driver endpoint repeats the owning day on every activity. It can
    // differ from the outer bucket, so the record value is authoritative.
    const anchor = numberOr(record?.day, 0) > 0 ? numberOr(record?.day, 0) : day;
    const startMinute = numberOr(record?.startTime, NaN);
    let endMinute = numberOr(record?.endTime, NaN);
    if (!Number.isFinite(startMinute) || !Number.isFinite(endMinute)) continue;
    if (endMinute < startMinute) endMinute += 24 * 60;

    const start = anchor + startMinute * MINUTE;
    const end = anchor + endMinute * MINUTE;
    if (!(end > start)) continue;

    const type = numberOr(record?.type, 0);
    out.push({
      type,
      typeName: text(record?.typeString) || TYPE_NAMES[type] || 'UNKNOWN',
      start,
      end,
      driverName,
      cardNumber,
      drivingStatus: numberOr(record?.drivingStatus, 0) || undefined,
      slot: true,
      entryType: text(record?.entryType) || 'Automatic',
    });
  }

  return sortActivities(out);
}

function driverVehicles(vehicles: unknown[] | null | undefined): VehiclePeriod[] {
  const out: VehiclePeriod[] = [];

  for (const raw of list(vehicles) as Record<string, unknown>[]) {
    const registrationNumber =
      text(raw?.['vehicleRegistrationNumber']) || text(raw?.['vehicleIdentificationNumber']);
    const start = numberOr(raw?.['vehicleFirstUse'], 0);
    const end = numberOr(raw?.['vehicleLastUse'], 0);
    if (!registrationNumber || !(end > start)) continue;
    out.push({
      registrationNumber,
      vehicleId: text(raw?.['vehicleIdentificationNumber']) || undefined,
      start,
      end,
    });
  }

  return out;
}

/* ------------------------------------------------------------------ */
/* Country helpers (driver endpoint, no `places` list)                 */
/* ------------------------------------------------------------------ */

function addCountryEvent(
  events: CountryEvent[],
  place: DriverCountryPlaceLike | null | undefined,
  kind: 'start' | 'end',
): void {
  if (!place) return;

  const time = numberOr(place.entryTime, 0);
  if (!(time > 0)) return;

  const country =
    kind === 'start'
      ? firstText(place.startCountry, place.country, place.endCountry)
      : firstText(place.endCountry, place.country, place.startCountry);
  if (!country) return;

  const codeValue =
    kind === 'start'
      ? place.startCountryCode ?? place.endCountryCode
      : place.endCountryCode ?? place.startCountryCode;
  const countryCode =
    typeof codeValue === 'string' && /[A-Za-z]/.test(codeValue) && codeValue.trim()
      ? codeValue.trim()
      : undefined;

  events.push({ country, countryCode, time });
}

/** One instant can carry both the start and the end place; keep the later one. */
function mergeCountryEvents(events: CountryEvent[]): CountryEvent[] {
  const byTime = new Map<number, CountryEvent>();
  for (const event of events) {
    byTime.set(Math.round(event.time), event);
  }
  return [...byTime.values()].sort((a, b) => a.time - b.time);
}

export function countryPeriodsFromEvents(events: CountryEvent[], periodEnd: number): CountryPeriod[] {
  const periods: CountryPeriod[] = [];

  events.forEach((event, index) => {
    const next = events[index + 1];
    const end = next ? next.time : periodEnd;
    if (!(end > event.time)) return;

    const previous = periods[periods.length - 1];
    if (previous && previous.country === event.country && previous.countryCode === event.countryCode) {
      previous.end = end;
    } else {
      periods.push({ country: event.country, countryCode: event.countryCode, start: event.time, end });
    }
  });

  return periods;
}

function lastActivityEnd(days: NormalizedDay[]): number {
  let end = 0;
  for (const day of days) {
    for (const activity of day.activities) {
      end = Math.max(end, activity.end);
    }
  }
  return end;
}

/* ------------------------------------------------------------------ */
/* Chart model (timezone applied here, and only here)                  */
/* ------------------------------------------------------------------ */

/**
 * Builds the chart model for the user's timezone.
 *
 * Rows are the local calendar days of the API day buckets. The bucket `day` is
 * a UTC midnight, so the row date comes from its UTC calendar date, while the
 * row window is local midnight → local midnight. Activity instants are kept as
 * absolute UTC values and the chart splits them at local midnight.
 *
 * `period` is the range the caller originally requested, before the fetch was
 * padded by a day on each side. Only the day buckets inside it become rows. The
 * padded days are still fed into the closest row's lane, so the pieces that fall
 * inside the requested local days are drawn and everything outside is dropped
 * by the chart.
 */
export function toChartData(
  diagram: NormalizedDiagram,
  timezone?: string,
  period?: TimeRange,
): ActivityChartData {
  const zone = safeTimeZone(timezone);
  const allDays = Array.isArray(diagram?.days) ? diagram.days : [];
  const days = fillPeriodDays(selectPeriodDays(allDays, period), period);
  const rows = new Map<string, ChartDay>();
  const rowOdo = new Map<string, { start: number | null | undefined; end: number | null | undefined }>();

  for (const day of days) {
    const start = localDayStart(day.date, zone);
    if (!Number.isFinite(start)) continue;

    const row: ChartDay = {
      day: start,
      date: day.date,
      meta: day.meta,
    };
    if (day.activities.length > 0) {
      row.lane = makeLane(day.activities, day.odoStart, day.odoEnd);
    }
    if (day.coDriverActivities.length > 0) {
      row.coDriverLane = makeLane(day.coDriverActivities, null, null);
    }
    rows.set(day.date, row);
    rowOdo.set(day.date, { start: day.odoStart, end: day.odoEnd });
  }

  if (rows.size > 0) {
    const first = days[0];
    const last = days[days.length - 1];

    // Padded days only contribute the pieces that land inside the requested
    // local days; attaching them to the nearest edge row lets the chart split
    // and clip them without creating extra rows.
    for (const day of allDays) {
      if (rows.has(day.date)) continue;
      const row = day.day < first.day ? rows.get(first.date) : rows.get(last.date);
      if (!row) continue;
      const odo = rowOdo.get(row.date ?? '');
      if (day.activities.length > 0) {
        appendActivities(row, day.activities, odo?.start, odo?.end);
      }
      if (day.coDriverActivities.length > 0) {
        appendCoDriverActivities(row, day.coDriverActivities);
      }
    }
  }

  const ordered = [...rows.values()].sort((a, b) => a.day - b.day);

  // Out-of-scope ranges and ferry markers are period-wide in the normalised
  // model; the chart splits them per local day. Attach every one of them to the
  // first row so no piece before the first row is lost.
  if (ordered.length > 0) {
    if (diagram.outOfScope?.length) ordered[0].outOfScope = diagram.outOfScope;
    if (diagram.ferry?.length) ordered[0].ferry = diagram.ferry;
  }

  return {
    days: ordered,
    countries: diagram.countries ?? [],
    countryEvents: diagram.countryEvents ?? [],
    driverPeriods: diagram.driverPeriods ?? [],
    coDriverPeriods: diagram.coDriverPeriods ?? [],
    vehicles: diagram.vehicles?.length ? diagram.vehicles : undefined,
  };
}

/**
 * Builds one chart row for a single local calendar day, no matter which API
 * day buckets the activities came from. Used by the daily report: the report
 * day is local, while the diagram buckets are UTC, so the first/last hours come
 * from the neighbouring buckets fetched with the padded request.
 */
export function toSingleDayChartData(
  diagram: NormalizedDiagram,
  timezone: string | undefined,
  date: string,
): ActivityChartData {
  const zone = safeTimeZone(timezone);
  const start = localDayStart(date, zone);
  const matching = diagram.days.find((day) => day.date === date);

  const row: ChartDay = {
    day: Number.isFinite(start) ? start : 0,
    date,
    meta: matching?.meta,
  };

  if (diagram.activities.length > 0) {
    row.lane = makeLane(diagram.activities, matching?.odoStart, matching?.odoEnd);
  }
  const coDriverActivities = diagram.days.flatMap((day) => day.coDriverActivities);
  if (coDriverActivities.length > 0) {
    row.coDriverLane = makeLane(coDriverActivities, null, null);
  }
  if (diagram.outOfScope?.length) row.outOfScope = diagram.outOfScope;
  if (diagram.ferry?.length) row.ferry = diagram.ferry;

  return {
    days: [row],
    countries: diagram.countries ?? [],
    countryEvents: diagram.countryEvents ?? [],
    driverPeriods: diagram.driverPeriods ?? [],
    coDriverPeriods: diagram.coDriverPeriods ?? [],
    vehicles: diagram.vehicles?.length ? diagram.vehicles : undefined,
  };
}

/**
 * Builds one chart row per requested local date, independently of the API day
 * buckets. Used by the daily report: its rows are the report's own days, not
 * the UTC bucket dates `toChartData` derives from the payload.
 *
 * Every activity is anchored to exactly one row (the local day its start falls
 * on), so it is never counted twice; the chart splits it at local midnight and
 * moves the other piece to the neighbouring row. An activity that started
 * before the first requested day but still runs into it is anchored to the
 * first row, so the day does not lose its first hours.
 */
export function toLocalDatesChartData(
  diagram: NormalizedDiagram,
  timezone: string | undefined,
  dates: string[],
): ActivityChartData {
  const zone = safeTimeZone(timezone);
  const ranges = new Map<string, { start: number; end: number }>();
  for (const date of dates) {
    const start = localDayStart(date, zone);
    if (!Number.isFinite(start)) continue;
    ranges.set(date, { start, end: nextLocalDayStart(start, zone) });
  }

  const ordered = [...ranges.keys()].sort();
  if (ordered.length === 0) {
    return { days: [] };
  }
  const first = ordered[0];
  const firstStart = ranges.get(first).start;

  const anchors = new Map<string, NormalizedActivity[]>();
  const coDriverAnchors = new Map<string, NormalizedActivity[]>();
  for (const date of ordered) {
    anchors.set(date, []);
    coDriverAnchors.set(date, []);
  }

  const anchorFor = (activity: NormalizedActivity): string | undefined => {
    const ownDate = localDateKey(activity.start, zone);
    if (anchors.has(ownDate)) return ownDate;
    // Carry the previous day's tail into the first row.
    if (activity.start < firstStart && activity.end > firstStart) return first;
    return undefined;
  };

  for (const activity of diagram.activities) {
    const date = anchorFor(activity);
    if (date) anchors.get(date).push(activity);
  }
  for (const activity of diagram.days.flatMap((day) => day.coDriverActivities)) {
    const date = anchorFor(activity);
    if (date) coDriverAnchors.get(date).push(activity);
  }

  const rows: ChartDay[] = ordered.map((date) => {
    const range = ranges.get(date);
    const matching = diagram.days.find((day) => day.date === date);
    const activities = anchors.get(date) ?? [];
    const coDriverActivities = coDriverAnchors.get(date) ?? [];
    const row: ChartDay = { day: range.start, date, meta: matching?.meta };
    if (activities.length > 0) {
      row.lane = makeLane(activities, matching?.odoStart, matching?.odoEnd);
    }
    if (coDriverActivities.length > 0) {
      row.coDriverLane = makeLane(coDriverActivities, null, null);
    }
    return row;
  });

  // Period-wide tracks are split per row by the chart; attach them once.
  if (diagram.outOfScope?.length) rows[0].outOfScope = diagram.outOfScope;
  if (diagram.ferry?.length) rows[0].ferry = diagram.ferry;

  return {
    days: rows,
    countries: diagram.countries ?? [],
    countryEvents: diagram.countryEvents ?? [],
    driverPeriods: diagram.driverPeriods ?? [],
    coDriverPeriods: diagram.coDriverPeriods ?? [],
    vehicles: diagram.vehicles?.length ? diagram.vehicles : undefined,
  };
}

/** Adds empty rows for selected dates the API did not return a bucket for. */
function fillPeriodDays(days: NormalizedDay[], period?: TimeRange): NormalizedDay[] {
  if (!period || !Number.isFinite(period.start) || !Number.isFinite(period.end)) return days;

  const byDate = new Map(days.map((day) => [day.date, day]));
  const from = Math.floor(period.start / DAY) * DAY;
  const to = Math.floor(period.end / DAY) * DAY;

  for (let start = from; start <= to; start += DAY) {
    const date = utcDate(start);
    if (!byDate.has(date)) {
      byDate.set(date, { day: start, date, activities: [], coDriverActivities: [], meta: {} });
    }
  }

  return [...byDate.values()].sort((a, b) => a.day - b.day);
}

function selectPeriodDays(days: NormalizedDay[], period?: TimeRange): NormalizedDay[] {
  if (!period || !Number.isFinite(period.start) || !Number.isFinite(period.end)) return days;

  const kept = filterDiagramDays(days, period);

  // A mismatch between the request and the returned buckets must not blank the
  // chart; fall back to everything the API sent.
  return kept.length > 0 ? kept : days;
}

function appendActivities(
  row: ChartDay,
  activities: NormalizedActivity[],
  odoStart?: number | null,
  odoEnd?: number | null,
): void {
  const lane = row.lane ?? (row.lane = emptyLane(odoStart, odoEnd));
  const named = activities.find((activity) => activity.driverName);
  if (named && !lane.driverName) {
    lane.driverName = named.driverName;
    lane.cardNumber = named.cardNumber;
  }
  lane.segments.push(...toSegments(activities));
}

function appendCoDriverActivities(row: ChartDay, activities: NormalizedActivity[]): void {
  const lane = row.coDriverLane ?? (row.coDriverLane = emptyLane());
  const named = activities.find((activity) => activity.driverName);
  if (named && !lane.driverName) {
    lane.driverName = named.driverName;
    lane.cardNumber = named.cardNumber;
  }
  lane.segments.push(...toSegments(activities));
}

function makeLane(
  activities: NormalizedActivity[],
  odoStart: number | null | undefined,
  odoEnd: number | null | undefined,
): ChartLane {
  const named = activities.find((activity) => activity.driverName);

  return {
    driverName: named?.driverName ?? '',
    cardNumber: named?.cardNumber ?? '',
    segments: toSegments(activities),
    odoStart: odoStart ?? null,
    odoEnd: odoEnd ?? null,
  };
}

function emptyLane(odoStart?: number | null, odoEnd?: number | null): ChartLane {
  return {
    driverName: '',
    cardNumber: '',
    segments: [],
    odoStart: odoStart ?? null,
    odoEnd: odoEnd ?? null,
  };
}

function toSegments(activities: NormalizedActivity[]): ChartSegment[] {
  return activities.map((activity) => ({
    type: activity.type,
    typeName: activity.typeName,
    start: activity.start,
    end: activity.end,
    entryType: activity.entryType,
    driverName: activity.driverName || undefined,
    cardNumber: activity.cardNumber || undefined,
    drivingStatus: activity.drivingStatus,
  }));
}

/* ------------------------------------------------------------------ */
/* Small helpers                                                       */
/* ------------------------------------------------------------------ */

function localDayStart(date: string, timeZone: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return NaN;
  return zonedEpoch(Number(match[1]), Number(match[2]), Number(match[3]), 0, 0, timeZone);
}

function safeTimeZone(timeZone?: string): string {
  const value = text(timeZone) || browserTimeZone();
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return value;
  } catch {
    return 'UTC';
  }
}

/** The viewer's timezone; the chart never silently renders in UTC. */
function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function utcDate(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toISOString().slice(0, 10);
}

function mergeDuplicateDays(days: NormalizedDay[]): NormalizedDay[] {
  const byDate = new Map<string, NormalizedDay>();

  for (const day of days) {
    const previous = byDate.get(day.date);
    if (!previous) {
      byDate.set(day.date, day);
      continue;
    }
    previous.activities.push(...day.activities);
    previous.coDriverActivities.push(...day.coDriverActivities);
    previous.meta = { ...previous.meta, ...day.meta };
    previous.odoStart = previous.odoStart ?? day.odoStart;
    previous.odoEnd = previous.odoEnd ?? day.odoEnd;
  }

  for (const day of byDate.values()) {
    day.activities = sortActivities(day.activities);
    day.coDriverActivities = sortActivities(day.coDriverActivities);
  }

  return [...byDate.values()].sort((a, b) => a.day - b.day);
}

function flatten(days: NormalizedDay[]): NormalizedActivity[] {
  return sortActivities(days.flatMap((day) => day.activities));
}

function sortActivities(activities: NormalizedActivity[]): NormalizedActivity[] {
  return activities.sort((a, b) => a.start - b.start || a.end - b.end);
}

function dedupePeriods(periods: DriverPeriod[]): DriverPeriod[] {
  const seen = new Set<string>();
  return periods.filter((period) => {
    const key = `${period.driverName}|${period.start}|${period.end}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).sort((a, b) => a.start - b.start);
}

function dedupeVehicles(vehicles: VehiclePeriod[]): VehiclePeriod[] {
  const seen = new Set<string>();
  return vehicles.filter((period) => {
    const key = `${period.registrationNumber}|${period.start}|${period.end}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).sort((a, b) => a.start - b.start);
}

function driverNames(
  roster: VehicleDriverLike[],
  activities: NormalizedActivity[],
  coDriverActivities: NormalizedActivity[],
): string {
  const names = new Set<string>();
  for (const driver of roster) {
    const name = text(driver?.driverName);
    if (name) names.add(name);
  }
  for (const activity of [...activities, ...coDriverActivities]) {
    if (activity.driverName) names.add(activity.driverName);
  }
  return [...names].join(' · ');
}

function ranges(items: { start?: unknown; end?: unknown }[] | null | undefined): TimeRange[] {
  const out: TimeRange[] = [];
  for (const item of list(items)) {
    const start = numberOr(item?.start, NaN);
    const end = numberOr(item?.end, NaN);
    if (!Number.isFinite(start) || !Number.isFinite(end) || !(end > start)) continue;
    out.push({ start, end });
  }
  return out;
}

function instants(items: unknown[] | null | undefined): number[] {
  return list(items)
    .map((item) => numberOr(item, NaN))
    .filter((value) => Number.isFinite(value) && value > 0);
}

function mergeRanges(ranges: TimeRange[]): TimeRange[] {
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: TimeRange[] = [];
  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end);
    } else {
      merged.push({ ...range });
    }
  }
  return merged;
}

function uniqueInstants(values: number[]): number[] {
  return [...new Set(values)].sort((a, b) => a - b);
}

function list<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function firstText(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function numberOr(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}
